import React, { useEffect, useMemo, useRef, useState, Children } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Package, Mail, LogOut, ArrowRight, CheckCircle2, Clock, Truck, Download, AlertCircle, X, MapPin, Plus, Minus, Trash2, Edit2, Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { attachOrderShippingAddress, getPreferredShippingAddress, recordProductSales, reconcileOrderInventory, subscribeToOrders, formatOrderNumber, type Address, type Order, type OrderLineItem, type ShippingAddress } from '../../../utils/firebase';
import { useCart } from '../../../contexts/CartContext';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { subscribeToCatalogProducts, type CatalogProduct } from '../../../utils/firebase';
import { bookData } from './bookData';
const CHECKOUT_ADDRESS_SNAPSHOT_KEY = 'sn_checkout_shipping_address';
function readCheckoutAddressSnapshot(): {
  sessionId: string;
  address: ShippingAddress;
} | null {
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY);
    if (!raw) return null;
    const snapshot = JSON.parse(raw) as {
      sessionId?: unknown;
      address?: Partial<ShippingAddress>;
    };
    if (typeof snapshot.sessionId !== 'string' || !snapshot.address || typeof snapshot.address.line1 !== 'string' || typeof snapshot.address.city !== 'string' || typeof snapshot.address.state !== 'string' || typeof snapshot.address.postalCode !== 'string' || typeof snapshot.address.country !== 'string') {
      return null;
    }
    return {
      sessionId: snapshot.sessionId,
      address: snapshot.address as ShippingAddress
    };
  } catch {
    return null;
  }
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function normalizeProductName(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/(\d+)/g, '').split(/[^a-z]+/).filter(Boolean).sort().join('');
}
function getItemImage(item: OrderLineItem, products: CatalogProduct[]): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') {
    const book = bookData.find(b => b.id === pid) ?? bookData.find(b => b.lookupKey && b.lookupKey === pid) ?? bookData.find(b => b.stripePriceId && b.stripePriceId === pid) ?? bookData.find(b => normalizeProductName(b.title) === normalizedName);
    return book?.coverImage;
  }
  const product = products.find(s => s.id === pid) ?? products.find(s => s.lookupKey && s.lookupKey === pid) ?? products.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? products.find(s => s.id === 'omega-3') : undefined);
  return product?.image;
}
function getItemShopId(item: OrderLineItem, products: CatalogProduct[]): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') return undefined;
  const product = products.find(s => s.id === pid) ?? products.find(s => s.lookupKey && s.lookupKey === pid) ?? products.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? products.find(s => s.id === 'omega-3') : undefined);
  return product?.id;
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      // Open the signed URL in a new tab so the user keeps their account page.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col items-start gap-2" data-id="element-1772" data-mp="27ogws3xc01cz">
      <button type="button" onClick={handleClick} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 rounded-md" data-id="element-1773" data-mp="27q1t9z37hudh">
        {loading ? <>
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1774" data-mp="3cuypblid8x0v" />
            Preparing…
          </> : <>
            <Download className="w-3.5 h-3.5" data-id="element-1775" data-mp="3ht9zjxckp9jm" />
            Download
          </>}
      </button>
      {error && <p className="text-[10px] text-burgundy leading-relaxed max-w-xs" data-id="element-1776" data-mp="3mmcuo9x00r1x">
          {error}
        </p>}
    </div>;
}
function FulfillmentBadge({
  order
}: {
  order: Order;
}) {
  const config = (() => {
    switch (order.fulfillmentStatus) {
      case 'digital_delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'shipped':
        return {
          icon: Truck,
          label: 'Shipped',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Processing',
          color: 'text-burgundy',
          bg: 'bg-burgundy/10'
        };
    }
  })();
  const Icon = config.icon;
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.color} text-[10px] font-bold uppercase tracking-[0.2em] rounded-md`} data-id="element-1777" data-mp="282p2fszhvpp2">
      <Icon className="w-3 h-3" data-id="element-1778" data-mp="2i2zhlj53b76v" />
      {config.label}
    </span>;
}
function EditInfoModal({
  currentName,
  currentEmail,
  onClose,
  onSaveName,
  onSaveEmail
}: {
  currentName: string;
  currentEmail: string;
  onClose: () => void;
  onSaveName: (name: string) => Promise<void>;
  onSaveEmail: (email: string, password: string) => Promise<void>;
}) {
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== currentEmail.toLowerCase();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (name.trim() !== currentName) {
        await onSaveName(name);
      }
      if (emailChanged) {
        if (!password) {
          throw new Error('Current password is required to change email.');
        }
        await onSaveEmail(email, password);
      }
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1779" data-mp="2mqfnit8e27rf">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1780" data-mp="2msjtdgpulguc" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1781" data-mp="2t820qpkayy3g">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1782" data-mp="2ej5vt1djm172">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1783" data-mp="2ehba3kug651x">Edit Information</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1784" data-mp="2ahspyrag9y05">
            <X className="w-5 h-5" data-id="element-1785" data-mp="25kamhgf7cazj" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1786" data-mp="20ibnsjueq28x">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1787" data-mp="1vjrzicsccede">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1788" data-mp="1lglhq8lps5g8">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1789" data-mp="1qewr6i83kdxx" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1790" data-mp="1vesnrwegt33g">
                Information Updated
              </p>
              {emailChanged && <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1791" data-mp="20cb07wroy0g7">
                  A verification link has been sent to your new email address.
                  The change will take effect once you confirm it.
                </p>}
            </div> : <>
              <div data-id="element-1792" data-mp="1cob8atin8401">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1793" data-mp="1cnrt3kkdntpp">
                  Full Name
                </label>
                <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1794" data-mp="1hg2pwejtmze1" />
              </div>

              <div data-id="element-1795" data-mp="6qbeakduu8wi">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1796" data-mp="bnbiepr591kz">
                  Email Address
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1797" data-mp="gng2sqv6aggh" />
              </div>

              {emailChanged && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="overflow-hidden" data-id="element-1798" data-mp="3p2g9fvv90mb6">
                  <div className="pt-2" data-id="element-1799" data-mp="1qf9iixc2dfw">
                    <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1800" data-mp="31cvr9f6x5t2y">
                      Current Password *
                    </label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1801" data-mp="35cea85f6ju35" />
                    <p className="text-xs text-[#5A5A5A] mt-2 leading-relaxed" data-id="element-1802" data-mp="2ow4fkeewidev">
                      For security, your current password is required to change
                      your email address.
                    </p>
                  </div>
                </motion.div>}

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1803" data-mp="2a3kc1cnua3ww">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1804" data-mp="256u113qw5yic" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1805" data-mp="215gt8o48nl8s">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1806" data-mp="1w7f9e0yznhw9">
                <button type="submit" disabled={loading || !name.trim() && !emailChanged} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1807" data-mp="1r5pyqmfoh68p">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1808" data-mp="17lj1lqcv009m" />
                      Saving...
                    </> : 'Save Changes'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
function ChangePasswordModal({
  onClose,
  onChangePassword
}: {
  onClose: () => void;
  onChangePassword: (current: string, next: string) => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onChangePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1809" data-mp="2jgeay7cotzt">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1810" data-mp="3unsexg7tysop" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1811" data-mp="19yl2yioaojcw">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1812" data-mp="2e8eikyfmh133">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1813" data-mp="2j9vfyjywe5ex">Change Password</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1814" data-mp="2o8yls6mpcfny">
            <X className="w-5 h-5" data-id="element-1815" data-mp="2s8p9g1gxmfo9" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1816" data-mp="2x0qtwao97cpd">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1817" data-mp="2x2tgslu40ppv">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1818" data-mp="321o9cfhvpsn6">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1819" data-mp="36wtsx7nt6v83" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1820" data-mp="3bxzrhm45hcpj">
                Password Changed
              </p>
              <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1821" data-mp="1081oeisocx4b">
                Your password has been successfully updated.
              </p>
            </div> : <>
              <div data-id="element-1822" data-mp="qcgqavqq8gzu">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1823" data-mp="qe1iwfthdpv2">
                  Current Password
                </label>
                <input type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1824" data-mp="1j15j87hmpg0i" />
              </div>

              <div data-id="element-1825" data-mp="14bpsusm0fclc">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1826" data-mp="14a59lz55bg2s">
                  New Password
                </label>
                <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1827" data-mp="1x059tkopc4zb" />
              </div>

              <div data-id="element-1828" data-mp="3hhwey7no3qs9">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1829" data-mp="3hgleohtfl15w">
                  Confirm New Password
                </label>
                <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1830" data-mp="38hht976sk9o8" />
              </div>

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1831" data-mp="33j6ld202498n">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1832" data-mp="wtrvtnl0ltz0" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1833" data-mp="11qid2e8k2vm0">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1834" data-mp="2m7hv6erzps0a">
                <button type="submit" disabled={loading || !currentPassword || !newPassword || !confirmPassword} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1835" data-mp="2hb13jall0vig">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1836" data-mp="34yisyzhdjhi9" />
                      Updating...
                    </> : 'Update Password'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
export function Account() {
  const {
    user,
    profile,
    loading: authLoading,
    signOut,
    resendVerification,
    updateDisplayName,
    updateEmail,
    changePassword
  } = useAuth();
  const navigate = useNavigate();
  const {
    items,
    clearCart
  } = useCart();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderSuccess = searchParams.get('order') === 'success';
  const [showSuccess, setShowSuccess] = useState(orderSuccess);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const attemptedLegacyAddressSyncs = useRef(new Set<string>());
  const preferredShippingAddress = useMemo(() => getPreferredShippingAddress(profile?.addresses), [profile?.addresses]);
  // Keep the exact return-session address in component state. The reconciliation
  // effect can safely clear sessionStorage without making the order card blank
  // while Firestore catches up with the order-address write.
  const [checkoutAddressSnapshot] = useState(readCheckoutAddressSnapshot);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const [showEditInfo, setShowEditInfo] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [printingOrderId, setPrintingOrderId] = useState<string | null>(null);
  useEffect(() => {
    if (!printingOrderId) return;
    document.body.classList.add('printing-single-order');
    const successNotification = document.getElementById('order-success-notification');
    if (successNotification) {
      successNotification.style.display = 'none';
    }
    const timer = setTimeout(() => {
      window.print();
      setPrintingOrderId(null);
      document.body.classList.remove('printing-single-order');
    }, 100);
    return () => {
      clearTimeout(timer);
      document.body.classList.remove('printing-single-order');
      if (successNotification) {
        successNotification.style.display = '';
      }
    };
  }, [printingOrderId]);
  const rawTab = searchParams.get('tab') as 'profile' | 'orders' | 'digital' | 'addresses' | 'cart' | null;
  const initialTab: 'profile' | 'orders' | 'digital' | 'addresses' | 'cart' | null = rawTab;
  const [activeTab, setActiveTab] = useState<'profile' | 'orders' | 'digital' | 'addresses' | 'cart'>(initialTab || 'orders');
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const handleResendVerification = async () => {
    setResendState('sending');
    setResendError(null);
    try {
      await resendVerification();
      setResendState('sent');
      setTimeout(() => setResendState('idle'), 6000);
    } catch (err: any) {
      setResendState('error');
      setResendError(err?.message ?? 'Could not send verification email. Try again.');
    }
  };
  useEffect(() => {
    return subscribeToCatalogProducts(setProducts);
  }, []);
  useEffect(() => {
    if (!orderSuccess) return;

    // Capture purchased supplements before clearing the cart.
    const supplementSales = items.filter(i => i.type === 'supplement').map(i => ({
      productId: i.productId,
      quantity: i.quantity
    }));
    if (supplementSales.length > 0) {
      recordProductSales(supplementSales).catch(err => console.error('[Sales] Failed to record product sales:', err));
    }

    // A successful Stripe return means this cart has been purchased.
    // Clear the shared CartContext immediately so the cart tab/header badge
    // update everywhere in the app.
    clearCart();

    // Remove the success marker from the URL, but preserve session_id because
    // the order/inventory reconciliation logic may still need it.
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, {
      replace: true
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSuccess]);
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: '/account'
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate]);
  // Subscribe to orders
  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError(null);
    const unsub = subscribeToOrders(user.uid, next => {
      setOrders(next);
      setOrdersLoading(false);
    });
    return unsub;
  }, [user]);
  useEffect(() => {
    if (!preferredShippingAddress) return;
    const legacyOrders = orders.filter(order => order.status === 'paid' && !order.shippingAddress?.line1 && order.lineItems.some(item => item.type === 'supplement') && !attemptedLegacyAddressSyncs.current.has(order.id));
    legacyOrders.forEach(order => {
      attemptedLegacyAddressSyncs.current.add(order.id);
      attachOrderShippingAddress(order.id, preferredShippingAddress).catch(error => {
        console.error('[Orders] Could not backfill shipping address:', error);
      });
    });
  }, [orders, preferredShippingAddress]);
  useEffect(() => {
    const sessionId = window.sessionStorage.getItem('sn_inventory_checkout_session');
    if (!sessionId) return;
    const paidOrder = orders.find(order => order.status === 'paid' && order.stripeSessionId === sessionId);
    if (!paidOrder) return;
    const checkoutAddress = readCheckoutAddressSnapshot();
    const addressMatchesOrder = checkoutAddress?.sessionId === sessionId;
    const attachAddress = addressMatchesOrder && !paidOrder.shippingAddress?.line1 ? attachOrderShippingAddress(paidOrder.id, checkoutAddress.address) : Promise.resolve();
    Promise.all([reconcileOrderInventory(paidOrder), attachAddress]).then(() => {
      window.sessionStorage.removeItem('sn_inventory_checkout_session');
      if (addressMatchesOrder) {
        window.sessionStorage.removeItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY);
      }
    }).catch(reconciliationError => {
      // Keep both checkout markers in place so a refresh retries safely.
      // Both writes are idempotent and never overwrite an existing address.
      console.error('[Checkout reconciliation] Failed:', reconciliationError);
    });
  }, [orders]);
  const handleSignOut = async () => {
    await signOut();
    navigate('/', {
      replace: true
    });
  };
  if (authLoading || !user) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center pt-32 pb-20" data-id="element-1837" data-mp="3alkeqxi1hypq">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1838" data-mp="c1vcbgvqxhoj" />
      </div>;
  }
  const displayName = profile?.displayName ?? user.displayName ?? 'there';
  const firstName = displayName.split(' ')[0];
  const displayOrders = orders.map(o => {
    const hasPhysicalItems = o.lineItems.some(item => item.type === 'supplement');
    const checkoutAddress = checkoutAddressSnapshot?.sessionId === o.stripeSessionId ? checkoutAddressSnapshot.address : undefined;
    return {
      ...o,
      // Prefer the immutable order snapshot. If it is absent, use the exact
      // address from this checkout return, then a saved address for legacy
      // orders while the backfill above repairs the order record.
      shippingAddress: o.shippingAddress?.line1 || !hasPhysicalItems ? o.shippingAddress : checkoutAddress ?? preferredShippingAddress,
      lineItems: o.lineItems.filter(i => activeTab === 'digital' ? i.type === 'book' : i.type !== 'book')
    };
  }).filter(o => o.lineItems.length > 0);
  return <div className="w-full min-h-screen bg-white" data-id="element-1839" data-mp="1l2vkfdzncils">
      <AnimatePresence data-id="element-1840" data-mp="1l1km3toz0322">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} id="order-success-notification" className="fixed top-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl print:hidden" data-id="element-1841" data-mp="1q1ysz8eu56yi">            <div className="bg-tealslate text-white shadow-2xl rounded-xl p-5 flex items-start gap-4" data-id="element-1842" data-mp="n0lx6a98tog">
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" data-id="element-1843" data-mp="3orgsmerbzrdi" />
              <div className="flex-1" data-id="element-1844" data-mp="3ott90nhfxtog">
                <p className="font-bold text-lg mb-1" data-id="element-1845" data-mp="3kqn5hnlpqglo">Order received.</p>
                <p className="text-sm text-white/90 leading-relaxed" data-id="element-1846" data-mp="3kshtjasa6otz">
                  Thanks for your purchase — your order will appear below in a
                  moment. A receipt has been emailed to you.
                </p>
              </div>
              <button onClick={() => setShowSuccess(false)} className="text-white/70 hover:text-white transition-colors" aria-label="Dismiss" data-id="element-1847" data-mp="bk5e9kq9wvfp">
                <X className="w-4 h-4" data-id="element-1848" data-mp="7hsj1q95kfx6" />
              </button>
            </div>
          </motion.div>}

        {showEditInfo && <EditInfoModal currentName={displayName} currentEmail={user.email ?? ''} onClose={() => setShowEditInfo(false)} onSaveName={updateDisplayName} onSaveEmail={updateEmail} data-id="element-1849" data-mp="gc6vzm67ned4" />}

        {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} onChangePassword={changePassword} data-id="element-1850" data-mp="qacxw3s0okhz" />}
      </AnimatePresence>

      {/* HEADER */}
      <section className="account-header pt-32 pb-8 px-6" data-id="element-1851" data-mp="19wo6q3yz7trw">
        <div className="max-w-5xl mx-auto" data-id="element-1852" data-mp="1dxrowx4hy1wa">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1853" data-mp="1dvdu8pdqarp9">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-1854" data-mp="14uyuwm3xjc2r">
              Welcome back, Jack!
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-1855" data-mp="uzfd13m7adx0">
              Manage your profile and orders
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-24 px-6" data-id="element-1856" data-mp="m47pfvn2c5tn">
        <div className="max-w-5xl mx-auto" data-id="element-1857" data-mp="78jdfkbi6ef7">
          {/* MAIN CONTENT */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-1858" data-mp="7be0ljlwidcb">
            <div className="account-tabs flex items-center gap-4 border-b border-[#E5E5E5] mb-8 overflow-x-auto hide-scrollbar" data-id="element-1859" data-mp="rpcqrhrhtbjx">
              <button onClick={() => {
              setActiveTab('profile');
              setSearchParams({
                tab: 'profile'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'profile' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1860" data-mp="iphaiyj2yqd1">
                Profile
              </button>
              <button onClick={() => {
              setActiveTab('addresses');
              setSearchParams({
                tab: 'addresses'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'addresses' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1861" data-mp="3w55ipj50gvg7">
                Addresses
              </button>
              <button onClick={() => {
              setActiveTab('orders');
              setSearchParams({});
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'orders' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1862" data-mp="3c1q3xywkix2l">
                Orders
              </button>
              <button onClick={() => {
              setActiveTab('digital');
              setSearchParams({
                tab: 'digital'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'digital' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1863" data-mp="2tb8eq4ay2jlz">
                Digital Orders
              </button>
              <button onClick={() => {
              setActiveTab('cart');
              setSearchParams({
                tab: 'cart'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'cart' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1864" data-mp="2aj5w4neqst93">
                Cart
              </button>
            </div>

            {activeTab === 'profile' ? <div className="space-y-8" data-id="element-1865" data-mp="10322cuooxjqv">
                <div className="max-w-2xl space-y-4" data-id="element-1866" data-mp="14vlerobn68u3">
                  <div className="flex items-center justify-between gap-4" data-id="element-1867" data-mp="14wedq34czu1o">
                    <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1868" data-mp="q7i62767sj1l">
                      Account Information
                    </h2>
                    <button onClick={() => setShowEditInfo(true)} className="shrink-0 text-xs font-bold py-2 px-3 bg-white border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate transition-colors flex items-center gap-2 shadow-sm" aria-label="Edit account information" data-id="element-1869" data-mp="h5henqxnwr7u">
                      <Edit2 className="w-3.5 h-3.5" data-id="element-1870" data-mp="3tpd2oyu9b3tq" />
                      Edit
                    </button>
                  </div>

                  <div className="bg-white border border-[#D1D1D1] rounded-md p-6 md:p-8" data-id="element-1871" data-mp="bk9pequ4zli8">
                    <dl className="divide-y divide-[#E5E5E5]" data-id="element-1872" data-mp="6jc8ehkv4xib">
                      <div className="flex items-center justify-between gap-4 py-3 first:pt-0" data-id="element-1873" data-mp="lhef30n6xz3s">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1874" data-mp="giteh0411det">
                          Name
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right" data-id="element-1875" data-mp="u96m11d0aq3m">
                          {displayName}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3" data-id="element-1876" data-mp="148xj9asybj7e">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1877" data-mp="za431ama5e3j">
                          Email
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right break-all flex items-center gap-2" data-id="element-1878" data-mp="x899ry8v3h5">
                          <Mail className="w-4 h-4 text-[#9A9A90] shrink-0" data-id="element-1879" data-mp="5vt8pp5o9j4j" />
                          {user.email}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3 last:pb-0" data-id="element-1880" data-mp="aow31zi9wwls">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1881" data-mp="foh3ked3dd0m">
                          Password
                        </dt>
                        <dd className="flex items-center gap-3" data-id="element-1882" data-mp="oo44z05tmito">
                          <span className="text-sm font-medium text-[#111111] tracking-widest" data-id="element-1883" data-mp="tmfhjini37fl">
                            ••••••••
                          </span>
                          <button onClick={() => setShowChangePassword(true)} className="account-action-button text-xs font-bold transition-colors" data-id="element-1884" data-mp="2qgk0dw178r54">
                            Change
                          </button>
                        </dd>
                      </div>
                    </dl>

                    {!user.emailVerified && <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-1885" data-mp="2e3omwz0lhwvv">
                        <div className="flex items-start gap-2 mb-4" data-id="element-1886" data-mp="2e1lnjodjyreh">
                          <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1887" data-mp="296g5x1r6nyw7" />
                          <p className="text-xs text-[#5A5A5A] leading-relaxed" data-id="element-1888" data-mp="36raok1q9irg3">
                            Email not verified. Check your inbox for a
                            verification link.
                          </p>
                        </div>
                        <button onClick={handleResendVerification} disabled={resendState === 'sending' || resendState === 'sent'} className="w-full text-xs font-bold py-3 px-4 border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-1889" data-mp="2st4a650x3k6t">
                          {resendState === 'sending' && <>
                              <span className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" data-id="element-1890" data-mp="3a2ohbv5svfnz" />
                              Sending…
                            </>}
                          {resendState === 'sent' && <>
                              <CheckCircle2 className="w-4 h-4" data-id="element-1891" data-mp="3o0d2z57oyqu3" />
                              Verification email sent
                            </>}
                          {(resendState === 'idle' || resendState === 'error') && <>
                              <Mail className="w-4 h-4" data-id="element-1892" data-mp="3ezn9w99tfek2" />
                              Resend verification email
                            </>}
                        </button>
                        {resendState === 'error' && resendError && <p className="text-xs text-burgundy mt-3 leading-relaxed" data-id="element-1893" data-mp="23jqblug37ia6">
                            {resendError}
                          </p>}
                        {resendState === 'sent' && <p className="text-xs text-[#9A9A90] mt-3 leading-relaxed" data-id="element-1894" data-mp="201izichxtlkg">
                            Check your inbox (and spam folder). The link expires
                            in 24 hours.
                          </p>}
                      </div>}
                  </div>
                </div>

                <button onClick={handleSignOut} className="w-full max-w-2xl bg-white border border-[#D1D1D1] shadow-sm rounded-xl p-5 hover:border-tealslate transition-all group flex items-center justify-between" data-id="element-1895" data-mp="252ylq7vupy4f">
                  <span className="text-sm font-bold group-hover:text-tealslate transition-colors text-[#FFFFFF]" data-id="element-1896" data-mp="1cbf0kifp29zs">
                    Sign Out
                  </span>
                  <LogOut className="w-4 h-4 text-[#9A9A90] group-hover:text-tealslate transition-colors" data-id="element-1897" data-mp="1maxiktc1nlp7" />
                </button>
              </div> : activeTab === 'addresses' ? <AddressBookTab data-id="element-1898" data-mp="tnjrhbv11kd0" /> : activeTab === 'orders' || activeTab === 'digital' ? <>
                {ordersLoading ? <div className="bg-white border border-[#E5E5E5] rounded-md p-12 flex items-center justify-center" data-id="element-1899" data-mp="1jbyvttmh51uw">
                    <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1900" data-mp="1nbg38slgo92p" />
                  </div> : ordersError ? <div className="bg-white border border-[#E5E5E5] rounded-md p-6 flex gap-3 items-start" data-id="element-1901" data-mp="1s5tucit3bu1k">
                    <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1902" data-mp="10cbapkryrdx4" />
                    <p className="text-sm text-burgundy" data-id="element-1903" data-mp="10eo0ul7vkul9">{ordersError}</p>
                  </div> : displayOrders.length === 0 ? <div className="bg-white border border-[#E5E5E5] rounded-md p-12 text-center" data-id="element-1904" data-mp="19czocedg3c9h">
                    <Package className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1905" data-mp="1ec2uk255voj1" />
                    <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1906" data-mp="1e9ztmn2h0azj">
                      {activeTab === 'digital' ? 'No digital orders yet.' : 'No physical orders yet.'}
                    </h3>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1907" data-mp="2pt2jqow7pn8u">
                      When you make a purchase, your orders will appear here.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1908" data-mp="975m1g8teter">
                      <Link to="/books" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1909" data-mp="4c9wcmr1wi4t">
                        Browse Books <ArrowRight className="w-4 h-4" data-id="element-1910" data-mp="3mjphgf37sufn" />
                      </Link>
                      <Link to="/shop" className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1911" data-mp="3mm29jl2osl6f">
                        Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1912" data-mp="1fsq4dv09zwhn" />
                      </Link>
                    </div>
                  </div> : <ul className="space-y-6" data-id="element-1913" data-mp="1as2a90gr2zj7">
                    {displayOrders.map(order => <li key={order.id} className={`order-item bg-white border border-[#D1D1D1] rounded-md overflow-hidden ${printingOrderId === order.id ? 'is-printing' : ''}`} data-id="element-1914" data-mp="jpchrd16l6ca">
                        {/* Header Strip */}
                        <div className="bg-[#F9F9F9] px-6 py-4 flex flex-wrap items-start justify-between gap-6" data-id="element-1915" data-mp="auei7n3j2vbz">
                          <div className="flex flex-wrap items-start gap-8 md:gap-12" data-id="element-1916" data-mp="yb9tzi320gl">
                            <div data-id="element-1917" data-mp="10ny0uefp723">
                              <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1918" data-mp="5xo61auugjvu">
                                Order Placed
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1919" data-mp="3s42uijd3ckw0">
                                {formatDate(order.createdAt)}
                              </p>
                            </div>
                            <div data-id="element-1920" data-mp="1qdrhfagcze4d">
                              <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1921" data-mp="1qby4xej2dzog">
                                Total
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1922" data-mp="36uq4ceoh5kow">
                                {formatPrice(order.total)}
                              </p>
                            </div>
                            {order.shippingAddress?.line1 && <>
                                {order.shippingAddress.recipientName && <div className="not-italic max-w-[220px]" data-id="element-1923" data-mp="3ui7z8j31cyw3">
                                    <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1924" data-mp="3pkprux4o2rq7">
                                      Ship To
                                    </p>
                                    <p className="text-sm font-medium text-[#111111]" data-id="element-1925" data-mp="2f8j06k4trxk">
                                      {order.shippingAddress.recipientName}
                                    </p>
                                  </div>}
                                <address className="not-italic max-w-[220px]" data-id="element-1926" data-mp="248o992cb54s0">
                                  <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1927" data-mp="ke6w9pr368po">
                                    Address
                                  </p>
                                  <p className="text-[#111111] text-[14px] w-[350px]" data-id="element-1928" data-mp="tev9ii8no51i">
                                    {order.shippingAddress.line1}
                                    {order.shippingAddress.line2 && <> {order.shippingAddress.line2}</>}
                                    <br data-id="element-1929" data-mp="ydevgin91gtv" />
                                    {order.shippingAddress.city},{' '}
                                    {order.shippingAddress.state}{' '}
                                    {order.shippingAddress.postalCode}
                                  </p>
                                </address>
                              </>}
                          </div>
                          <div className="text-right" data-id="element-1930" data-mp="1h3wn0jwci6dn">
                            <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1931" data-mp="1h57ogd2bj21l">
                              Order # {formatOrderNumber(order.id)}
                            </p>
                            <button onClick={() => setPrintingOrderId(order.id)} className="order-details-button print-button text-sm text-[#0056b3] hover:text-[#004494] hover:underline font-medium transition-colors" data-id="element-1932" data-mp="25mgkco3e6l7e">
                              Order Details
                            </button>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-1933" data-mp="1mqzzs02funnl">
                          
                          <div className="flex-1 w-full" data-id="element-1934" data-mp="1czm1fvm83tw1">
                            {(order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered' || order.fulfillmentStatus === 'shipped' || order.trackingNumber) && <div className="mb-6" data-id="element-1935" data-mp="131xvsan2x1z3">
                                {(order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered' || order.fulfillmentStatus === 'shipped') && <h3 className="text-lg font-bold text-tealslate mb-1" data-id="element-1936" data-mp="30xd2bj5al89x">
                                    {order.fulfillmentStatus === 'shipped' ? 'Shipped' : 'Delivered'}
                                  </h3>}
                                {order.trackingNumber && <p className="text-sm text-[#5A5A5A] flex items-center gap-1.5" data-id="element-1937" data-mp="2qxduxpklznyn">
                                    <Truck className="w-4 h-4" data-id="element-1938" data-mp="3ol4nzdcq2xnv" />
                                    {order.trackingCarrier ?? 'Tracking'} ·{' '}
                                    {order.trackingNumber}
                                  </p>}
                              </div>}

                            <ul className="space-y-8" data-id="element-1939" data-mp="3amwlkpwx345b">
                              {order.lineItems.map((item, idx) => {
                        const image = getItemImage(item, products);
                        return <li key={idx} className="flex flex-col sm:flex-row items-center gap-6" data-id="element-1940" data-mp="1z7hs3w3x2uey" data-mpi={idx}>
                                    {/* Left side: image */}
                                    {image && <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-1941" data-mp="ir29v6wfxfqi">
                                        <img src={image} alt={item.name} className="max-w-full max-h-full object-contain" data-id="element-1942" data-mp="npdi5d737i6m" />
                                      </div>}

                                    {/* Right side: details and actions */}
                                    <div className="flex-1" data-id="element-1943" data-mp="12ft2yqvlco49">
                                    {item.type === 'book' ? <Link to="/books" className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-base mb-1 block transition-colors" data-id="element-1944" data-mp="12hnqmcy8yuc1">
                                        {item.name}
                                      </Link> : <Link to={`/shop/${getItemShopId(item, products) || item.productId}`} className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-base mb-1 block transition-colors" data-id="element-1945" data-mp="389ay04ostai7">
                                        {item.name}
                                      </Link>}
                                      <p className="text-sm text-[#5A5A5A] mb-4" data-id="element-1946" data-mp="2cxky24lryvbw">
                                        Qty {item.quantity}
                                        {item.type === 'book' && ' · Digital'}{' '}
                                        ·{' '}
                                        {formatPrice(item.unitPrice * item.quantity)}
                                      </p>

                                      {item.type === 'book' && order.status === 'paid' && <DownloadButton orderId={order.id} data-id="element-1947" data-mp="1u6ab1yj672pa" />}
                                    </div>
                                  </li>;
                      })}
                            </ul>
                          </div>

                          {/* Order Summary */}
                          <div className="w-full sm:w-80 shrink-0 bg-[#FFFFFF] p-5" data-id="element-1948" data-mp="1gedjpwpbswty">
                            <h3 className="font-bold text-[#111111] mb-4 font-[Avenir,_sans-serif]" data-id="element-1949" data-mp="1bhdf7q84k24y">
                              Order Summary
                            </h3>

                            <div className="space-y-3 text-sm" data-id="element-1950" data-mp="3rwh236nowbz5">
                              <div className="flex justify-between text-[#5A5A5A]" data-id="element-1951" data-mp="3d7b57pcbqxuf">
                                <span data-id="element-1952" data-mp="3i5vx91hfab7s">Subtotal</span>
                                <span data-id="element-1953" data-mp="3i6fcgafouli4">{formatPrice(order.subtotal)}</span>
                              </div>

<div className="flex justify-between text-[#5A5A5A]" data-id="element-1954" data-mp="9r43hodgzaoa">
  <span data-id="element-1955" data-mp="ep5k82nfaxwl">Shipping</span>
  <span data-id="element-1956" data-mp="epp1ri95vka3">
    {(order.shipping ?? 0) > 0 ? formatPrice(order.shipping ?? 0) : 'Free'}
  </span>
</div>

                              <div className="flex justify-between text-[#5A5A5A]" data-id="element-1957" data-mp="ouq8qqth377">
                                <span data-id="element-1958" data-mp="4pofpojwqq27">Tax</span>
                                <span data-id="element-1959" data-mp="2lfmut8d3wle8">{formatPrice(order.tax ?? 0)}</span>
                              </div>

                              {(order.discount ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-1960" data-mp="2pi9qxlgt8sh8">
                                  <span data-id="element-1961" data-mp="vyrc7ik6ofp5">Discount</span>
                                  <span data-id="element-1962" data-mp="w0khouj28tvk">-{formatPrice(order.discount ?? 0)}</span>
                                </div>}

                              <div className="pt-3 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111]" data-id="element-1963" data-mp="m8okdlost7se">
                                <span data-id="element-1964" data-mp="hada59ulcv9n">Grand Total</span>
                                <span data-id="element-1965" data-mp="h7r4jfr9gpu2">{formatPrice(order.total)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>)}
                  </ul>}
              </> : <CartTab data-id="element-1966" data-mp="3avimmpwv3wl" />}
          </motion.div>
        </div>
      </section>
    </div>;
}
function AddressBookTab() {
  const {
    profile,
    updateProfile
  } = useAuth();
  const addresses = profile?.addresses ?? [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const handleSetDefault = async (id: string) => {
    const updated = addresses.map(a => ({
      ...a,
      isDefault: a.id === id
    }));
    await updateProfile({
      addresses: updated
    });
  };
  const handleDelete = async (id: string) => {
    const updated = addresses.filter(a => a.id !== id);
    // If we deleted the default, make the first remaining one default
    if (addresses.find(a => a.id === id)?.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    await updateProfile({
      addresses: updated
    });
  };
  if (isAdding || editingId) {
    const addressToEdit = editingId ? addresses.find(a => a.id === editingId) : null;
    return <AddressForm initialData={addressToEdit} onCancel={() => {
      setIsAdding(false);
      setEditingId(null);
    }} onSave={async addr => {
      let updated: Address[];
      if (editingId) {
        updated = addresses.map(a => a.id === editingId ? {
          ...addr,
          id: editingId,
          isDefault: a.isDefault
        } : a);
      } else {
        const isFirst = addresses.length === 0;
        updated = [...addresses, {
          ...addr,
          id: Math.random().toString(36).substring(2, 9),
          isDefault: isFirst
        }];
      }
      await updateProfile({
        addresses: updated
      });
      setIsAdding(false);
      setEditingId(null);
    }} data-id="element-1967" data-mp="1r60d4dxts8tu" />;
  }
  return <div className="space-y-6" data-id="element-1968" data-mp="1i9k9noblhgm">
      <div className="flex items-center justify-between" data-id="element-1969" data-mp="g88ifqfvksw3">
        <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1970" data-mp="g6np245arfzt">
          Saved Addresses
        </h2>
        <button onClick={() => setIsAdding(true)} className="px-4 py-2 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate transition-colors rounded-md shadow-sm flex items-center gap-2" data-id="element-1971" data-mp="bbi3j6ft43c9">
          <Plus className="w-4 h-4" data-id="element-1972" data-mp="2r0a1da78l151" />
          Add New
        </button>
      </div>

      {addresses.length === 0 ? <div className="bg-white border border-[#E5E5E5] rounded-md p-12 text-center" data-id="element-1973" data-mp="2m1yqcw57gkl8">
          <MapPin className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1974" data-mp="11jo8d6vg50l4" />
          <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1975" data-mp="11lhgkqlpjsv8">
            No saved addresses.
          </h3>
          <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1976" data-mp="16kbwnksaj3bn">
            Add a shipping address to speed up your checkout process.
          </p>
          <button onClick={() => setIsAdding(true)} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1977" data-mp="wr4vyd8hwbhw">
            Add Address
          </button>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1978" data-mp="9ul43r3k7q8h">
          {addresses.map(addr => <div key={addr.id} className="bg-white border border-[#D1D1D1] rounded-md p-6 flex flex-col" data-id="element-1979" data-mp="du3lwe033l73">
              <div className="flex-1" data-id="element-1980" data-mp="24456kmxttlgz">
                <div className="flex items-start justify-between mb-2" data-id="element-1981" data-mp="288cwuojxn9hb">
                  <p className="font-bold text-[#111111]" data-id="element-1982" data-mp="3awz25phd7k1q">
                    {addr.recipientName}
                  </p>
                  {addr.isDefault && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold text-[#FFFFFF] bg-[#172F3D]" data-id="element-1983" data-mp="3fx4w5cb3ximz">
                      Default
                    </span>}
                </div>
                <p className="text-sm text-[#5A5A5A] leading-relaxed mb-6" data-id="element-1984" data-mp="3op6sozi22erz">
                  {addr.line1}
                  {addr.line2 && <br data-id="element-1985" data-mp="3tpku3yp5iqh1" />}
                  {addr.line2}
                  <br data-id="element-1986" data-mp="2g48n0mgjc0b" />
                  {addr.city}, {addr.state} {addr.postalCode}
                  <br data-id="element-1987" data-mp="7fqolhto76ct" />
                  {addr.country}
                </p>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-[#E5E5E5]" data-id="element-1988" data-mp="bidirpdtd3d6">
                <button onClick={() => setEditingId(addr.id)} className="account-action-button text-sm font-bold text-[#5A5A5A] hover:text-tealslate transition-colors flex items-center gap-1.5" data-id="element-1989" data-mp="297wmm51oc58j">
                  <Edit2 className="w-3.5 h-3.5" data-id="element-1990" data-mp="25pphx85utasu" /> Edit
                </button>
                <button onClick={() => handleDelete(addr.id)} className="account-action-button delete-action text-sm font-bold text-[#5A5A5A] hover:text-burgundy transition-colors flex items-center gap-1.5" data-id="element-1991" data-mp="25rk1oj5e2wvc">
                  <Trash2 className="w-3.5 h-3.5" data-id="element-1992" data-mp="1ryavssgngthk" /> Delete
                </button>
                {!addr.isDefault && <button onClick={() => handleSetDefault(addr.id)} className="account-action-button ml-auto text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1993" data-mp="2ool7252uuyzt">
                    Set as Default
                  </button>}
              </div>
            </div>)}
        </div>}
    </div>;
}
function AddressForm({
  initialData,
  onCancel,
  onSave
}: {
  initialData: Address | null;
  onCancel: () => void;
  onSave: (addr: Omit<Address, 'id' | 'isDefault'>) => Promise<void>;
}) {
  const [recipientName, setRecipientName] = useState(initialData?.recipientName ?? '');
  const [line1, setLine1] = useState(initialData?.line1 ?? '');
  const [line2, setLine2] = useState(initialData?.line2 ?? '');
  const [city, setCity] = useState(initialData?.city ?? '');
  const [state, setState] = useState(initialData?.state ?? '');
  const [postalCode, setPostalCode] = useState(initialData?.postalCode ?? '');
  const [country, setCountry] = useState(initialData?.country ?? 'United States');
  const [deliveryInstructions, setDeliveryInstructions] = useState(initialData?.deliveryInstructions ?? '');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await onSave({
      recipientName: recipientName.trim(),
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
      country: country.trim(),
      deliveryInstructions: deliveryInstructions.trim()
    });
    setSubmitting(false);
  };
  return <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1994" data-mp="3gjw1ex8ft5c2">
      <h2 className="text-xl font-bold text-[#111111] mb-6 tracking-tight" data-id="element-1995" data-mp="3gke3iahs6drw">
        {initialData ? 'Edit Address' : 'Add New Address'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1996" data-mp="3plc1oc5jfr2k">
        <div data-id="element-1997" data-mp="3pipuidmk7dkv">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1998" data-mp="3lj8mbcfr02c0">
            Recipient Full Name *
          </label>
          <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1999" data-mp="3d2r3m0a9rwq" />
        </div>
        <div data-id="element-2000" data-mp="85dpuljaf7np">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2001" data-mp="84uhnwjfvxmv">
            Address Line 1 *
          </label>
          <input type="text" required value={line1} onChange={e => setLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2002" data-mp="m20xpjcs3i8e" />
        </div>
        <div data-id="element-2003" data-mp="2gi7p9vzttp4i">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2004" data-mp="2ghzeqar6evwh">
            Address Line 2 (Optional)
          </label>
          <input type="text" value={line2} onChange={e => setLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2005" data-mp="2pcvtdito10rq" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2006" data-mp="21mjpcj1jjo6f">
          <div data-id="element-2007" data-mp="21p5wwiofm3on">
            <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2008" data-mp="26m4pgwsg62kq">
              City *
            </label>
            <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2009" data-mp="3d2q4nvyc6h9v" />
          </div>
          <div className="grid grid-cols-2 gap-5" data-id="element-2010" data-mp="3n5n4mzu2npcf">
            <div data-id="element-2011" data-mp="3n6x1jlp6urhh">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2012" data-mp="3n8hq8uotng7f">
                State / Province *
              </label>
              <input type="text" required value={state} onChange={e => setState(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2013" data-mp="3eemrq5i7go0r" />
            </div>
            <div data-id="element-2014" data-mp="2zhm0e33xev2t">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2015" data-mp="2zh2s7e42vl1z">
                ZIP / Postal *
              </label>
              <input type="text" required value={postalCode} onChange={e => setPostalCode(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2016" data-mp="2vhbwcvzp2eql" />
            </div>
          </div>
        </div>
        <div data-id="element-2017" data-mp="2ry3are06g3l9">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2018" data-mp="2rxtifjrntkce">
            Country *
          </label>
          <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2019" data-mp="2e7hp5hjc9fjo" />
        </div>
        <div data-id="element-2020" data-mp="3av4gj11k27q9">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2021" data-mp="3atjn5eqz8utz">
            Delivery Instructions (Optional)
          </label>
          <textarea rows={3} value={deliveryInstructions} onChange={e => setDeliveryInstructions(e.target.value)} placeholder="e.g. Leave at the front porch, gate code #1234, ring the doorbell." className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] placeholder:text-[#9A9A90] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-2022" data-mp="31upqxphk66gy" />
          <p className="text-xs text-[#9A9A90] mt-2 leading-relaxed" data-id="element-2023" data-mp="1dwor3ovgval8">
            Help the courier find you — access codes, drop-off spots, or
            anything else they should know.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-4" data-id="element-2024" data-mp="1myxza839irvg">
          <button type="submit" disabled={submitting} className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-2025" data-mp="z7sldygvu9ns">
            {submitting ? 'Saving...' : 'Save Address'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-2026" data-mp="147ne87k7x9bh">
            Cancel
          </button>
        </div>
      </form>
    </div>;
}
function CartTab() {
  const {
    items,
    subtotal,
    itemCount,
    removeItem,
    updateQuantity
  } = useCart();
  const navigate = useNavigate();
  if (items.length === 0) {
    return <div className="bg-white border border-[#D1D1D1] rounded-md p-12 text-center" data-id="element-2027" data-mp="3w272bnw9gqki">
        <div className="w-16 h-16 bg-[#F9F9F9] border border-[#E5E5E5] rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-2028" data-mp="4xgsb7ecwqma">
          <Package className="w-7 h-7 text-[#9A9A90]" data-id="element-2029" data-mp="8xzlp5vwe4ql" />
        </div>
        <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-2030" data-mp="dwu2616e7h7j">
          Your cart is empty.
        </h3>
        <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-sm mx-auto leading-relaxed" data-id="element-2031" data-mp="ipn6wp1v2pjm">
          Browse the bookshop or shop to add items.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-2032" data-mp="smrwkyux1356">
          <Link to="/books" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-2033" data-mp="skov9iobbnl5">
            Browse Books <ArrowRight className="w-4 h-4" data-id="element-2034" data-mp="2d4ka547uz2ma" />
          </Link>
          <Link to="/shop" className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-2035" data-mp="2d2zgdgtdbnph">
            Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-2036" data-mp="30uejvcwvsb2b" />
          </Link>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-2037" data-mp="2vuiillfkixqg">
      <div data-id="element-2038" data-mp="2vvk4kky4h4my">
        <h2 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-2039" data-mp="2vvtt043ir3qj">
          Shopping Cart
        </h2>
      </div>

      <div className="bg-white border border-[#D1D1D1] rounded-md" data-id="element-2040" data-mp="2m58ndwpfgyy4">
        {/* Column header */}
        <div className="px-6 py-3 flex items-center justify-end" data-id="element-2041" data-mp="3jkmipitjsfcp">
          <span className="text-sm font-medium text-[#9A9A90]" data-id="element-2042" data-mp="3jm7grif2mgfb">Price</span>
        </div>

        <ul data-id="element-2043" data-mp="3et4kh2iwssvf">
          {items.map(item => {
          const image = getItemImage(item as unknown as OrderLineItem);
          return <li key={item.productId} className="px-6 py-6 flex gap-5" data-id="element-2044" data-mp="9ns0bqb1yjk">
                {/* Image */}
                {image && <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-2045" data-mp="3rkecc17oep7z">
                    <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-2046" data-mp="3ihdaicpufka4" />
                  </div>}

                {/* Details */}
                <div className="flex-1 min-w-0" data-id="element-2047" data-mp="j0x5ew3757gf">
                  {item.type === 'book' ? <Link to="/books" className="text-base sm:text-lg font-bold text-[#0056b3] hover:text-[#004494] hover:underline leading-snug block transition-colors" data-id="element-2048" data-mp="e4ggw0s34r2t">
                      {item.name}
                    </Link> : <Link to={`/shop/${getItemShopId(item as unknown as OrderLineItem) || item.productId}`} className="text-base sm:text-lg font-bold text-[#0056b3] hover:text-[#004494] hover:underline leading-snug block transition-colors" data-id="element-2049" data-mp="11spzktu04ypq">
                      {item.name}
                    </Link>}

                  {/* Controls row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mt-4" data-id="element-2050" data-mp="2ko47ab9t53nb">
                    <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-2051" data-mp="2ooo052ncll3n">
                      <button onClick={() => {
                    if (item.quantity === 1) {
                      removeItem(item.productId);
                    } else {
                      updateQuantity(item.productId, item.quantity - 1);
                    }
                  }} className={`px-3 py-1.5 transition-colors ${item.quantity === 1 ? 'text-[#5A5A5A] hover:bg-burgundy/10 hover:text-burgundy' : 'text-[#5A5A5A] hover:bg-[#F9F9F9]'}`} aria-label={item.quantity === 1 ? 'Remove item' : 'Decrease quantity'} data-id="element-2052" data-mp="2tq3nx2gwu5pq">
                        {item.quantity === 1 ? <Trash2 className="w-4 h-4" data-id="element-2053" data-mp="3mcy43n6p2ixe" /> : <Minus className="w-4 h-4" data-id="element-2054" data-mp="3mdzjg3uqtrkx" />}
                      </button>
                      <span className="px-4 text-sm font-bold text-[#111111] min-w-[2rem] text-center" data-id="element-2055" data-mp="1ma6tkqq2dg0q">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-3 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-2056" data-mp="1w0hv66m9zd4g">
                        <Plus className="w-4 h-4" data-id="element-2057" data-mp="2516ar5rbhljg" />
                      </button>
                    </div>

                    <span className="w-px h-5 bg-[#E5E5E5] hidden sm:block" data-id="element-2058" data-mp="210e1wdmcxinp" />

                    <button onClick={() => removeItem(item.productId)} className="cart-delete-button text-sm font-medium text-[#5A5A5A] hover:text-burgundy transition-colors inline-flex items-center gap-1.5" data-id="element-2059" data-mp="2ezm0b4ap4mqu">
                      <Trash2 className="w-4 h-4" data-id="element-2060" data-mp="klcokrj0wufk" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="shrink-0 text-right" data-id="element-2061" data-mp="2r9v8k8bvtdl1">
                  <p className="text-base sm:text-lg font-bold text-[#111111]" data-id="element-2062" data-mp="2rb6ae2lrob9i">
                    {formatPrice(item.unitPrice * item.quantity)}
                  </p>
                </div>
              </li>;
        })}
        </ul>

        {/* Subtotal line */}
        <div className="px-6 py-5 text-right" data-id="element-2063" data-mp="2mivd77iev3kn">
          <p className="text-lg text-[#111111]" data-id="element-2064" data-mp="2mhk9f7oytvtj">
            Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
            <span className="font-bold" data-id="element-2065" data-mp="28lq7x9qiu8qg">{formatPrice(subtotal)}</span>
          </p>
        </div>
      </div>

      {/* Checkout block */}
      <div className="mt-6" data-id="element-2066" data-mp="1ylzelbdp5zry">
        <p className="text-sm text-[#5A5A5A] mb-4 text-right" data-id="element-2067" data-mp="1ymr01tdkyh4i">
          Shipping and taxes calculated at checkout.
        </p>
        <button onClick={() => navigate('/checkout-v2')} className="w-full px-8 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3 text-[18px]" data-id="element-2068" data-mp="23m3w94qcd0jr">
          Checkout 
        </button>
      </div>
    </div>;
}